/**
 * GateNotesRollup — read-only aggregation of the reviewer notes left on an
 * intermediate gate's upstream agent tabs. This is where the notes-only
 * agent tabs "show up in the gate" (labelled by which agent they came from).
 * Renders nothing when the group has no notes.
 */
import { STAGE_LABELS, type PausedStage } from '@/types/enums';
import { extractReviewerNote, latestAttemptByStage } from './StageHistoryRowHelpers';
import { ReviewerNoteCallout } from './ReviewerNoteCallout';
import { upstreamStagesFor } from './gateGroups';
import type { AgentResult } from '@/types';

interface Props {
  /** 'gate_1' | 'gate_2'. Anything else renders nothing. */
  gate: string;
  agents: AgentResult[];
}

interface GroupNote {
  stage: PausedStage;
  note: string;
  at: string | null;
}

export function GateNotesRollup({ gate, agents }: Props) {
  const byStage = latestAttemptByStage(agents);
  const notes: GroupNote[] = [];
  for (const stage of upstreamStagesFor(gate)) {
    const row = byStage.get(stage);
    const note = extractReviewerNote(row?.edits_json ?? null);
    if (note) notes.push({ stage, note, at: row?.approved_at ?? null });
  }

  if (notes.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Notes from upstream stages ({notes.length})
      </p>
      {notes.map(({ stage, note, at }) => (
        <ReviewerNoteCallout key={stage} note={note} at={at} label={STAGE_LABELS[stage]} />
      ))}
    </div>
  );
}
